import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../button';
import { Dialog, DialogTitle, DialogBody } from '../dialog';
import { useOrgMiembros } from '../../hooks/useOrgMiembros';
import { useOtAcciones, useOtConfig } from '../../hooks/useOrdenesTrabajo';
import {
  MOTIVO_MAX_LEN, folioTexto, fasesDe, destinoRechazo, validarAprobacion, APROBACION_RESULTADOS,
} from '../../utils/ordenesTrabajo';

const field =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none ' +
  'focus:border-azul-surelia focus:ring-1 focus:ring-azul-surelia';

/**
 * Reject an approval: motivo required, the OT goes back to the fase before
 * (default) or to any earlier one, to the people who worked it there.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Object|null} props.ot
 */
export function OtRechazarDialog({ isOpen, onClose, ot }) {
  const { miembros } = useOrgMiembros();
  const { config } = useOtConfig();
  const { resolverAprobacion } = useOtAcciones();
  const fases = useMemo(() => fasesDe(config, ot?.tipo), [config, ot?.tipo]);
  const porDefecto = useMemo(() => destinoRechazo(ot, fases), [ot, fases]);

  const [faseId, setFaseId] = useState('');
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setFaseId(porDefecto.faseId || '');
    setMotivo('');
    setError(null);
    setBusy(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, ot?.id]);

  if (!isOpen || !ot) return null;

  const idxActual = fases.findIndex((f) => f.id === ot.faseId);
  const anteriores = fases.filter((_, i) => i < idxActual);
  const nombreDe = (uid) => miembros.find((m) => m.id === uid)?.nombre || uid;
  const regresaA = faseId === porDefecto.faseId ? porDefecto.asignados : [];

  const confirmar = async () => {
    const msg = validarAprobacion({ resultado: APROBACION_RESULTADOS.RECHAZADA, motivo });
    if (msg) { setError(msg); return; }
    if (!faseId) { setError('Elige a qué fase regresa.'); return; }
    setBusy(true);
    try {
      await resolverAprobacion(ot, { resultado: APROBACION_RESULTADOS.RECHAZADA, motivo: motivo.trim(), faseId, asignados: regresaA });
      onClose();
    } catch (e) {
      console.error('rechazo failed', e);
      setError(e.message || 'No se pudo guardar.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? () => {} : onClose} size="sm">
      <DialogTitle><span className="text-azul-surelia">Rechazar {folioTexto(ot.folio)}</span></DialogTitle>
      <DialogBody>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Motivo</label>
            <textarea rows={3} value={motivo} maxLength={MOTIVO_MAX_LEN} onChange={(e) => setMotivo(e.target.value)} className={field} autoFocus />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Regresa a</label>
            <select value={faseId} onChange={(e) => setFaseId(e.target.value)} className={field}>
              {anteriores.length === 0 && <option value="">No hay fase anterior</option>}
              {anteriores.map((f) => <option key={f.id} value={f.id}>{f.nombre}</option>)}
            </select>
            {regresaA.length > 0 && (
              <div className="mt-1 text-xs text-zinc-500">Con {regresaA.map(nombreDe).join(', ')}, que la trabajaron ahí.</div>
            )}
          </div>
          {error && <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{error}</div>}
          <div className="flex justify-end gap-2 pt-1">
            <Button plain onClick={onClose} disabled={busy}>Cancelar</Button>
            <Button color="surelia" onClick={confirmar} disabled={busy || !faseId}>{busy ? 'Guardando…' : 'Rechazar'}</Button>
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
}

export default OtRechazarDialog;
